// src/context/persona/personaModel.js
//
// PURE helpers behind the structured persona editor (GET|PUT
// /personas/{id}/sections). No React, no network. The backend owns the rules;
// these checks only save the user a round trip — the server still decides.

// Fields that must hold ONE line (the backend: `…:must_be_one_line`).
export const SINGLE_LINE = ['name', 'who.role', 'who.context'];
export const ITEM_FIELDS = ['pain', 'gain', 'indicators'];

export const emptyItem = () => ({ pain: '', gain: '', indicators: '' });

export function emptyHalf(format) {
  return {
    name: '',
    who: { summary: '', role: '', context: '' },
    pain_gain: format === 'list'
      ? { format: 'list', items: [emptyItem()], text: null }
      : { format: 'prose', items: null, text: '' },
    language_use: '',
    language_avoid: '',
  };
}

// Consulted only when CREATING (or switching market in create mode): an existing
// persona keeps the Pain & Gain format the server holds.
export const formatForMarket = (market) => (market === 'b2c' ? 'prose' : 'list');
// ⚠️ `any` (v197, "fits B2B and B2C") is a LIST market like `b2b`: only `b2c` is prose.

// ⛔ The id rule is the BACKEND's, mirrored: `ENTITY_ID_RE` is
// `^[a-z0-9][a-z0-9_-]{0,49}$`. Checking it here turns a 422 into a field error
// the user can fix while typing; the server still decides. (Lives here, not in
// NewPersonaPanel, so the editor can use it without a circular import.)
export const PERSONA_ID_RE = /^[a-z0-9][a-z0-9_-]{0,49}$/;

// A slug is a CONVENIENCE, never the authority: an unslugifiable name (all
// accents, all punctuation) simply yields an empty box and a named refusal
// rather than an id we invent.
export function slugify(name) {
  return String(name)
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase().replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '').slice(0, 50);
}

// v201 — the manual wizard's id, never shown: the slug of the name, or "persona" for a name with no usable
// letters, and — when that is taken — the first free "-2", "-3"… The base is cut so the suffix fits the 50
// characters, and never ends with a hyphen. Always matches PERSONA_ID_RE. "Taken" is what the caller knows (the
// listing, plus ids a create-only save was refused for); the server's 412 remains the authority.
const PERSONA_ID_MAX = 50;
const FALLBACK_PERSONA_ID = 'persona';
export function uniquePersonaId(name, takenIds = []) {
  const taken = new Set(takenIds);
  const base = slugify(name) || FALLBACK_PERSONA_ID;
  if (!taken.has(base)) return base;
  for (let n = 2; ; n += 1) {
    const suffix = `-${n}`;
    const candidate = `${base.slice(0, PERSONA_ID_MAX - suffix.length).replace(/-+$/, '')}${suffix}`;
    if (!taken.has(candidate)) return candidate;
  }
}

// v201 — two personas may not share a NAME (they would read the same in every list and in the generator's persona
// selector). Compared as people read them: case, accents and repeated spaces do not make a name different.
const nameKey = (name) => String(name || '')
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .toLowerCase().replace(/\s+/g, ' ').trim();
export function isNameTaken(name, existingNames = []) {
  const key = nameKey(name);
  return key !== '' && existingNames.some((other) => nameKey(other) === key);
}

// Every user-typed string of a half, as [path, value] — the paths match the
// backend's error paths (`who.role`, `pain_gain.items.0.gain`, `pain_gain.text`).
export function halfEntries(half) {
  const out = [
    ['name', half.name],
    ['who.summary', half.who.summary],
    ['who.role', half.who.role],
    ['who.context', half.who.context],
    ['language_use', half.language_use],
    ['language_avoid', half.language_avoid],
  ];
  if (half.pain_gain.format === 'list') {
    (half.pain_gain.items || []).forEach((it, n) => {
      ITEM_FIELDS.forEach(f => out.push([`pain_gain.items.${n}.${f}`, it[f]]));
    });
  } else {
    out.push(['pain_gain.text', half.pain_gain.text]);
  }
  return out;
}

const blank = (v) => !String(v ?? '').trim();

export function isHalfEmpty(half) {
  return !half || halfEntries(half).every(([, v]) => blank(v));
}

const isSingleLine = (path) => SINGLE_LINE.includes(path) || /^pain_gain\.items\.\d+\./.test(path);

/** Client-side checks → { '<lang>.<path>': reasonKey }. `lang` is 'it' | 'en'. */
export function checkHalf(half, lang) {
  const errs = {};
  for (const [path, value] of halfEntries(half)) {
    const key = `${lang}.${path}`;
    const v = String(value ?? '');
    if (blank(v)) { errs[key] = 'required'; continue; }
    if (isSingleLine(path) && v.trim().includes('\n')) { errs[key] = 'oneLine'; continue; }
    if (v.split(/\n\s*\n/).some(p => p.trim().startsWith('#'))) { errs[key] = 'noHash'; continue; }
    if (path.endsWith('.indicators') && v.includes('*')) errs[key] = 'noAsterisks';
  }
  if (half.pain_gain.format === 'list' && !(half.pain_gain.items || []).length) {
    errs[`${lang}.pain_gain`] = 'atLeastOneRow';
  }
  return errs;
}

/** All client errors for a draft {market, it, en}; EN is skipped when entirely empty. */
export function checkDraft(draft) {
  const errs = { ...checkHalf(draft.it, 'it') };
  if (draft.en && !isHalfEmpty(draft.en)) Object.assign(errs, checkHalf(draft.en, 'en'));
  return errs;
}

// ── v198: base / advanced view and the manual wizard ────────────────────────
//
// BASE = name, who.summary, language_use, language_avoid. ADVANCED = who.role, who.context and the whole Pain &
// Gain section (`pain_gain`, `pain_gain.text`, `pain_gain.items.N.*`). ⛔ Advanced fields stay REQUIRED: the
// split decides what is SHOWN, never what is validated. Paths are WITHOUT the language prefix.
export function isAdvancedPath(path) {
  return path === 'who.role' || path === 'who.context' || path === 'pain_gain' || path.startsWith('pain_gain.');
}

const stripLang = (key) => key.replace(/^(it|en)\./, '');

/** The manual wizard's steps, in order. */
export const WIZARD_STEPS = ['identity', 'profile', 'needs'];

/** '<lang>.<path>' (or a bare path) → the wizard step that shows that field. */
export function wizardStepOf(key) {
  const path = stripLang(key);
  if (path === 'name') return 'identity';
  return isAdvancedPath(path) ? 'needs' : 'profile';
}

/** The earliest wizard step any of these error keys belongs to; null for none. */
export function earliestStep(keys) {
  const at = keys.map((k) => WIZARD_STEPS.indexOf(wizardStepOf(k))).filter((i) => i >= 0);
  return at.length ? WIZARD_STEPS[Math.min(...at)] : null;
}

/** Only the errors a given wizard step shows. */
export function errorsForStep(errors, step) {
  return Object.fromEntries(Object.entries(errors).filter(([k]) => wizardStepOf(k) === step));
}

/** True when any error key points at a field the base view hides. */
export function hasAdvancedError(errors) {
  return Object.keys(errors || {}).some((k) => isAdvancedPath(stripLang(k)));
}

/**
 * How many hidden fields need attention in the base view of `lang`: its errors on advanced paths, plus — on the
 * Italian half only — the AI's number warnings on advanced paths (warnings are keyed by bare Italian paths).
 */
export function hiddenCount({ errors = {}, warnings = {}, lang }) {
  const errs = Object.keys(errors).filter((k) => k.startsWith(`${lang}.`) && isAdvancedPath(stripLang(k))).length;
  const warns = lang === 'it' ? Object.keys(warnings).filter(isAdvancedPath).length : 0;
  return errs + warns;
}

/** The PUT body. An entirely empty EN half is sent as null — unless the GET had one. */
export function buildBody(draft, { hadEn }) {
  const trimHalf = (h) => JSON.parse(JSON.stringify(h, (k, v) => (typeof v === 'string' ? v.trim() : v)));
  const en = draft.en && (!isHalfEmpty(draft.en) || hadEn) ? trimHalf(draft.en) : null;
  return { market: draft.market, it: trimHalf(draft.it), en };
}

const REASONS = {
  string_too_short: 'required', missing: 'required',
  must_be_one_line: 'oneLine', 'paragraph_starts_with_#': 'noHash', no_asterisks: 'noAsterisks',
};

/**
 * Server 422s → { fields: {'<lang>.<path>': reasonKey}, general: [reasonKey] }.
 * BRIEF_VALIDATION_ERROR: details.validation_errors[].loc = ['body', lang, ...path].
 * CONTEXT_ENTITY_INVALID: details.errors[] = '<lang>.<path>:<reason>'.
 * ⛔ Never returns the server's `msg` / message.
 */
export function mapServerErrors(err) {
  const fields = {};
  const general = [];
  const d = err?.details || {};
  (d.validation_errors || []).forEach((v) => {
    const loc = (v.loc || []).filter(p => p !== 'body');
    const reason = REASONS[v.type] || (v.type === 'value_error' ? 'shape' : 'invalid');
    if (loc.length >= 2 && (loc[0] === 'it' || loc[0] === 'en')) fields[loc.join('.')] = reason;
    else general.push(reason);
  });
  (d.errors || []).forEach((s) => {
    const str = String(s);
    const colon = str.lastIndexOf(':');
    const where = colon > 0 ? str.slice(0, colon) : str;
    const reason = REASONS[str.slice(colon + 1)] || 'invalid';
    if (/^(it|en)\.round_trip/.test(where) || !/^(it|en)\./.test(where)) general.push('structure');
    else fields[where] = reason;
  });
  return { fields, general };
}

/** The GET → an editable draft; `en` becomes an empty half of the IT format when absent. */
export function draftFromDetail(detail) {
  const clone = (x) => JSON.parse(JSON.stringify(x));
  const it = clone(detail.it);
  return { market: detail.market, it, en: detail.en ? clone(detail.en) : emptyHalf(it.pain_gain.format) };
}
